import React, { useMemo } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import truncate from 'lodash/truncate';
import { DNABox, DNAText, Iffy, DNAIcon, DNATextLink } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  CommonIcon,
  DNAChip,
  DNAConditionalButton,
  DNACustomDeckThumbnail,
  DNACustomFieldChipList,
  DNADocumentThumbnail,
  useDNACustomDeckActions,
} from 'src/components/DNA';
import { CustomDeckORM, DocumentVersionORM } from 'src/types/types';
import { isCustomDeckORM, isDocumentVersionORM } from 'src/types/typeguards';
import { useDispatch } from 'src/state/redux';
import { DocumentVersionUpdateInfo, DocumentVersionUpdateInfoPath } from 'src/state/redux/selector/folder';
import { useAppSettings } from 'src/state/context/AppSettings';
import { DNAMyUpdatesButtonActions, FolderItemStatus } from './DNAMyUpdatesButtonActions';
import { useHistory } from 'react-router';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';

const S = StyleSheet.create({
  documentListRowContainer: {
    borderColor: colors['color-gray-80'],
    borderWidth: 1,
  },
  documentListRowTitle: {
    paddingHorizontal: 12,
    paddingBottom: 0,
    paddingVertical: 8,
    borderColor: colors['color-gray-80'],
    borderBottomWidth: 1,
    backgroundColor: colors['color-ui2-neutral-50'],
  },
  updatedItemPathContainer: {
    marginRight: 16,
    flex: 1,
  },
  UpdatedItemPathItem: {
    paddingVertical: 4,
    maxWidth: '100%',
  },
  folderIcon: {
    marginHorizontal: 8,
  },
  caretIcon: {
    color: colors['color-ui2-neutral-400'],
    height: 14,
    width: 14,
    marginLeft: 8,
    marginRight: 8,
    paddingTop: 2,
  },
  badge: {
    marginHorizontal: 4,
  },
  thumbnailContainer: {
    borderWidth: 1,
    borderColor: colors['color-ui2-neutral-200'],
    backgroundColor: colors['color-ui2-neutral-50'],
  },
  detailContainer: {
    paddingVertical: 16,
  },
})

const MAX_FOLDER_LENGTH = 80
const MAX_DOCUMENT_LENGTH = 40

interface UpdatedItemPathItemProps {
  updateInfo: DocumentVersionUpdateInfo,
  path: DocumentVersionUpdateInfoPath,
  index: number,
}

interface UpdatedItemPathProps {
  updateInfo: DocumentVersionUpdateInfo,
}

interface UpdatedItemListRowProps {
  updateInfo: DocumentVersionUpdateInfo,
  setFolderItems: React.Dispatch<React.SetStateAction<DocumentVersionUpdateInfo[]>>,
  documentVersionORM?: DocumentVersionORM,
}

interface ItemInfoProps {
  updateInfo: DocumentVersionUpdateInfo,
}
const ItemInfo: React.FC<ItemInfoProps> = (props) => {
  const { updateInfo } = props;
  const itemORM = updateInfo.folderItem.relations.itemORM;
  const isCustomDeck = isCustomDeckORM(itemORM);
  const isDocument = isDocumentVersionORM(itemORM);
  const dispatch = useDispatch();
  const customDeckActions = useDNACustomDeckActions();
  const isInReview = updateInfo.status === FolderItemStatus.Review;

  const handleDocumentClick = (e?: any) => {
    e?.stopPropagation?.();
    if (isDocument) {
      dispatch(contentPreviewModalActions.setModalVisibility({
        documentVersionId: itemORM.model.id,
        folderItemORM: updateInfo.folderItem,
        content: updateInfo.folderItem,
        isOpen: true,
        isFullWindow: false,
      }));
    } else if (isCustomDeck && (
      isInReview ||
      updateInfo.status === FolderItemStatus.AutoUpdated ||
      updateInfo.status === FolderItemStatus.Outdated
    )) {
      customDeckActions.edit(itemORM as CustomDeckORM, updateInfo.folderItem, 'REVIEW')();
    }
  };

  return (
    <DNABox appearance="row" spacing="sm" style={ S.detailContainer }>
      <Pressable onPress={handleDocumentClick}>
        <DNABox style={S.thumbnailContainer} alignY="center" alignX="center">
          <DNABox
            style={{ position: 'absolute', top: 0, left: 0, width: 120, height: 68 }}
            alignY="center"
            alignX="center"
          >
            <DNAIcon.Styled
              appearance="outline"
              status="subtle"
              size="md"
              name="file-x-regular"
            />
          </DNABox>
          <Iffy is={isCustomDeck}>
            <DNACustomDeckThumbnail.Hover
              customDeckORM={itemORM as CustomDeckORM}
              width={120}
              height={68}
            />
          </Iffy>

          <Iffy is={isDocument}>
            <DNADocumentThumbnail.Hover
              documentVersionORM={itemORM as DocumentVersionORM}
              width={120}
              height={68}
            />
          </Iffy>
        </DNABox>
      </Pressable>
      <DNABox appearance="col" spacing="sm">
        <DNATextLink
          status="primary"
          numberOfLines={1}
          onPress={handleDocumentClick}
        >
          {truncate(updateInfo.folderItem.meta.title, {
            length: MAX_DOCUMENT_LENGTH,
          })}
        </DNATextLink>
        <DNABox alignY="center" spacing="sm">
          <Iffy is={isCustomDeck}>
            <DNAChip.Beacon variant="MODIFIED" />
          </Iffy>
          <Iffy is={isDocument}>
            <DNACustomFieldChipList documentVersionORM={itemORM as DocumentVersionORM} />
          </Iffy>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

const UpdatedItemPathItem: React.FC<UpdatedItemPathItemProps> = (props) => {
  const { updateInfo, path, index } = props;
  const iconColor = useMemo(() => colors['color-ui2-neutral-500'], []);
  const history = useHistory();

  const handleFolderClick = (e?: any) => {
    e?.stopPropagation?.();
    if (path.id) {
      const pathToFolder = updateInfo.path
        .slice(0, index + 1)
        .map(p => p.id)
        .filter(id => id)
        .join('/');

      const targetUrl = `/folders/${pathToFolder}`;
      history.push(targetUrl);
    }
  };

  return (
    <Pressable onPress={handleFolderClick}>
      <DNABox alignY="center" style={S.UpdatedItemPathItem}>
        <DNAIcon.Styled
          appearance="ghost"
          name="folder-simple-fill"
          size="md"
          style={S.folderIcon}
          color={iconColor}
        />
        <DNATextLink
          status="primary"
          numberOfLines={1}
          onPress={handleFolderClick}
        >
          {truncate(path.title, {
            length: MAX_FOLDER_LENGTH,
          })}
        </DNATextLink>
        <Iffy is={index < updateInfo.path.length - 1}>
          <DNAIcon.Styled name="caret-right" style={S.caretIcon} />
        </Iffy>
      </DNABox>
    </Pressable>
  )
}

const UpdatedItemPath: React.FC<UpdatedItemPathProps> = (props) => {
  const { updateInfo } = props;
  return (
    <DNABox style={S.updatedItemPathContainer} wrap="start">
      {updateInfo.path.map((path, index) => (
        <UpdatedItemPathItem key={`${path}_${index}`} updateInfo={updateInfo} path={path} index={index} />
      ))}
    </DNABox>
  )
}

const UpdatedItemListRow: React.FC<UpdatedItemListRowProps> = (props) => {
  const { updateInfo, setFolderItems } = props;
  const { deviceMode, isOnline } = useAppSettings();
  const dispatch = useDispatch();
  const customDeckActions = useDNACustomDeckActions();

  const handleAction = (e?: any) => {
    e?.stopPropagation?.();
    DNAMyUpdatesButtonActions[updateInfo.status].action?.(dispatch, [updateInfo], customDeckActions)

    // Item status was updated in action, now we update the array reference
    setFolderItems(items => [...items])
  }

  const showSuccessText = [FolderItemStatus.Acknowledged, FolderItemStatus.Updated]
    .includes(updateInfo.status)

  const reviewUnavailable = useMemo(() =>
    (updateInfo.status === FolderItemStatus.Review) &&
    !isOnline &&
    isCustomDeckORM(updateInfo.folderItem.relations.itemORM) &&
    !updateInfo.folderItem.relations.itemORM.meta.assets.isContentCached
  , [updateInfo, isOnline])

  return (
    <DNABox fill alignY="center" style={S.documentListRowContainer} appearance="col">
      <DNABox fill childFill={0} alignY="center" style={S.documentListRowTitle}>
        {/* ITEM PATH */}
        <UpdatedItemPath updateInfo={updateInfo} />
      </DNABox>
      <DNABox spacing="between" alignY="center" style={{ paddingHorizontal: 16, backgroundColor: 'white' }}>
        <ItemInfo updateInfo={updateInfo} />
        {/* ACTION BUTTONS */}
        <DNABox alignX="end">
          <Iffy is={showSuccessText}>
            <DNABox alignY="center">
              <CommonIcon variant="check" />
              <DNAText status="success" style={{ fontSize: 14 }}>
                {DNAMyUpdatesButtonActions[updateInfo.status].label}
              </DNAText>
            </DNABox>
          </Iffy>
          <Iffy is={!showSuccessText}>
            <DNAConditionalButton
              size={deviceMode === 'desktop' ? 'md' : 'xl'}
              status={updateInfo.status === 'Review' ? 'warning' : 'tertiary'}
              appearance={updateInfo.status === 'Review' ? 'filled' : 'outline'}
              onPress={handleAction}
            >
              <DNAConditionalButton.Disable condition={reviewUnavailable}>
                <DNAText colorMode="dark">This file must be synced prior to review.</DNAText>
              </DNAConditionalButton.Disable>
              {DNAMyUpdatesButtonActions[updateInfo.status].label}
            </DNAConditionalButton>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

export default UpdatedItemListRow
